import { useEffect, useRef } from "react";

const WS_BASE = process.env.REACT_APP_IG_ADAPTER_WS_URL;

/**
 * useIgWebSocket — koneksi native WebSocket ke ig-adapter.
 *
 * @param {function} onMessage - callback saat event new_message diterima.
 * @param {function} [onChatAssigned] - callback saat event chat_assigned diterima.
 * @param {function} [onChatUnassigned] - callback saat event chat_unassigned diterima.
 * @param {function} [onUnreadUpdate] - callback saat event unread_update diterima.
 * @param {function} [onChatRead] - callback saat event chat_read diterima.
 * @param {function} [onSystemMessage] - callback saat event system_message diterima.
 * @param {string} [userId] - CRM user ID — dipakai server untuk targeted delivery.
 * @param {boolean} [canSeeAll] - true untuk Super Admin, System Admin, Admin, CS (Access_All_Dept).
 *
 * Fitur:
 * - Auto-reconnect dengan exponential backoff (2s → 4s → 8s → max 30s)
 * - Disconnect bersih saat component unmount
 */
export function useIgWebSocket(onMessage, onChatAssigned, onChatUnassigned, onUnreadUpdate, onChatRead, onSystemMessage, userId, canSeeAll) {
  const wsRef = useRef(null);
  const retryDelayRef = useRef(2000);
  const stoppedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const onChatAssignedRef = useRef(onChatAssigned);
  const onChatUnassignedRef = useRef(onChatUnassigned);
  const onUnreadUpdateRef = useRef(onUnreadUpdate);
  const onChatReadRef = useRef(onChatRead);
  const onSystemMessageRef = useRef(onSystemMessage);

  // Selalu gunakan callback terbaru tanpa restart koneksi
  useEffect(() => {
    onMessageRef.current = onMessage;
    onChatAssignedRef.current = onChatAssigned;
    onChatUnassignedRef.current = onChatUnassigned;
    onUnreadUpdateRef.current = onUnreadUpdate;
    onChatReadRef.current = onChatRead;
    onSystemMessageRef.current = onSystemMessage;
  });

  useEffect(() => {
    stoppedRef.current = false;

    const buildWsUrl = () => {
      const params = new URLSearchParams();
      if (userId) params.set("userId", userId);
      if (canSeeAll) params.set("seeAll", "1");
      const qs = params.toString();
      return `${WS_BASE}/ws${qs ? `?${qs}` : ""}`;
    };

    const connect = () => {
      if (stoppedRef.current) return;

      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[ig-ws] connected", { userId, canSeeAll });
        retryDelayRef.current = 2000;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_message" && onMessageRef.current) {
            onMessageRef.current(data.message);
          } else if (data.type === "chat_assigned" && onChatAssignedRef.current) {
            onChatAssignedRef.current(data);
          } else if (data.type === "chat_unassigned" && onChatUnassignedRef.current) {
            onChatUnassignedRef.current(data);
          } else if (data.type === "unread_update" && onUnreadUpdateRef.current) {
            onUnreadUpdateRef.current(data);
          } else if (data.type === "chat_read" && onChatReadRef.current) {
            onChatReadRef.current(data);
          } else if (data.type === "system_message" && onSystemMessageRef.current) {
            onSystemMessageRef.current(data);
          }
        } catch (e) {
          console.error("[ig-ws] parse error", e);
        }
      };

      ws.onclose = () => {
        if (stoppedRef.current) return;
        const delay = retryDelayRef.current;
        console.log(`[ig-ws] disconnected, retry in ${delay}ms`);
        setTimeout(() => {
          retryDelayRef.current = Math.min(delay * 2, 30000);
          connect();
        }, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      stoppedRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userId, canSeeAll]); // eslint-disable-line react-hooks/exhaustive-deps
}
